import { useLocation } from 'preact-iso'
import { routes } from '@/utils/routes'
import { EmailStatusBadge } from './EmailStatusBadge'
import { whenLabel } from './emailActivityFormat'
import type { EmailActivityRowDto } from '@/types/emailActivity'

interface Props {
    items: EmailActivityRowDto[]
    clientCompanyId: string
    workspaceId: string
    onRowClick: (row: EmailActivityRowDto) => void
    page: number
    total: number
    pageSize: number
    onPageChange: (page: number) => void
}

// The per-recipient activity table + its pagination footer. A row opens the detail flyout (onRowClick); the
// recipient name links straight to the ORB-383 contact route (lead anchor else audience FileImportRow).
export function EmailActivityTable({ items, clientCompanyId, workspaceId, onRowClick, page, total, pageSize, onPageChange }: Props) {
    const { route } = useLocation()
    const scoped = routes.scoped(clientCompanyId, workspaceId)

    const rowContactHref = (row: EmailActivityRowDto): string | null => {
        const anchorType = row.LeadId ? 'lead' : row.FileImportRowId ? 'audience' : null
        const anchorId = row.LeadId ?? row.FileImportRowId
        if (!anchorType || !anchorId || !row.CampaignId) return null
        return scoped.campaignContact(row.CampaignId, anchorType, anchorId, 'emails')
    }

    const rangeStart = total === 0 ? 0 : page * pageSize + 1
    const rangeEnd = Math.min((page + 1) * pageSize, total)
    const hasNext = (page + 1) * pageSize < total

    return (
        <>
            <div class="overflow-x-auto">
                <table class="w-full text-sm">
                    <thead>
                        <tr class="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400 dark:border-slate-800 dark:text-slate-500">
                            <th class="px-6 py-2.5 font-semibold">Recipient</th>
                            <th class="px-3 py-2.5 font-semibold">Email</th>
                            <th class="px-3 py-2.5 font-semibold">Status</th>
                            <th class="hidden px-3 py-2.5 font-semibold lg:table-cell">Context</th>
                            <th class="px-6 py-2.5 text-right font-semibold">When</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100 dark:divide-slate-800/70">
                        {items.map(row => {
                            const href = rowContactHref(row)
                            return (
                                <tr key={row.Id} onClick={() => onRowClick(row)} class="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                    <td class="px-6 py-3">
                                        {href ? (
                                            <a href={href} onClick={e => { e.stopPropagation(); e.preventDefault(); route(href) }} class="font-medium text-primary-700 hover:underline dark:text-primary-400">{row.RecipientName || row.RecipientEmail}</a>
                                        ) : (
                                            <span class="font-medium text-slate-900 dark:text-white">{row.RecipientName || row.RecipientEmail}</span>
                                        )}
                                        {row.RecipientName && <span class="block text-xs text-slate-400 dark:text-slate-500">{row.RecipientEmail}</span>}
                                    </td>
                                    <td class="px-3 py-3">
                                        <span class="text-slate-800 dark:text-slate-200">{row.Subject || '—'}</span>
                                        <span class="ml-2 inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">{row.EmailType}</span>
                                        {row.FailureReason && <span class="block text-xs text-slate-400 dark:text-slate-500">{row.FailureReason}</span>}
                                    </td>
                                    <td class="px-3 py-3"><EmailStatusBadge status={row.Status} /></td>
                                    <td class="hidden px-3 py-3 text-slate-500 dark:text-slate-400 lg:table-cell">{[row.CampaignName, row.WebName].filter(Boolean).join(' · ') || '—'}</td>
                                    <td class="px-6 py-3 text-right tabular-nums text-slate-600 dark:text-slate-300">{whenLabel(row)}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
            <div class="flex items-center justify-between gap-3 border-t border-slate-200 px-6 py-3 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <span>{total === 0 ? 'No emails' : `${rangeStart}–${rangeEnd} of ${total.toLocaleString()}`}</span>
                <div class="flex items-center gap-1">
                    <button type="button" disabled={page === 0} onClick={() => onPageChange(Math.max(0, page - 1))}
                        class="rounded border border-slate-200 px-2 py-1 font-medium text-slate-600 enabled:hover:bg-slate-50 disabled:text-slate-300 dark:border-slate-700 dark:text-slate-300 dark:enabled:hover:bg-slate-800 dark:disabled:text-slate-600">Prev</button>
                    <button type="button" disabled={!hasNext} onClick={() => onPageChange(page + 1)}
                        class="rounded border border-slate-200 px-2 py-1 font-medium text-slate-600 enabled:hover:bg-slate-50 disabled:text-slate-300 dark:border-slate-700 dark:text-slate-300 dark:enabled:hover:bg-slate-800 dark:disabled:text-slate-600">Next</button>
                </div>
            </div>
        </>
    )
}
